import { z } from "zod";
// Response and request shapes come from the generated contract validators. Type-only imports keep
// this module free of runtime contract code: forms and pure helpers here never re-declare them.
import type {
  edgeGroupAdmissionSchema,
  edgeGroupApprovalPolicySchema,
  edgeGroupApprovalResultSchema,
  edgeGroupChangeRequestSchema,
  edgeGroupChangeSchema,
  edgeGroupDraftRequestSchema,
  edgeGroupJoinTokenIssuedSchema,
  edgeGroupJoinTokenRequestSchema,
  edgeGroupJoinTokenSchema,
  edgeGroupNamesRequestSchema,
  edgeGroupReleasesSchema,
  edgeGroupRemovalRequestSchema,
  edgeGroupRolloutSchema,
  edgeGroupSchema,
  edgeGroupUpdateRequestSchema,
  edgeGroupWithdrawalRequestSchema,
} from "@/generated/edge-approval-contract";

export type EdgeGroup = z.infer<typeof edgeGroupSchema>;
export type Capability = EdgeGroup["configuration"]["capabilities"][number]["capability"];
export type GroupEdge = EdgeGroup["edges"][number];
export type Coverage = EdgeGroup["coverage"][number];
export type GroupChange = z.infer<typeof edgeGroupChangeSchema>;
export type Admission = z.infer<typeof edgeGroupAdmissionSchema>;
export type Release = z.infer<typeof edgeGroupReleasesSchema>["releases"][number];
export type JoinToken = z.infer<typeof edgeGroupJoinTokenSchema>;
export type IssuedJoinToken = z.infer<typeof edgeGroupJoinTokenIssuedSchema>;
export type JoinTokenRequest = z.infer<typeof edgeGroupJoinTokenRequestSchema>;
export type NamesRequest = z.infer<typeof edgeGroupNamesRequestSchema>;
export type GroupNames = NonNullable<EdgeGroup["names"]>;

/** The forwarder change request an administrator hands their DNS team, as text. */
export function forwarderChangeRequest(names: GroupNames): string {
  return [
    `Change: add a conditional forwarder for ${names.forwarder.zone}`,
    `Forward to: ${names.forwarder.servers.join(", ")}`,
    "Only this zone is forwarded; any of these Edges answers for every group of the tenant.",
    ...names.forwarder.resolver,
    "Verify from an agent host:",
    ...names.forwarder.verify.map((line) => `  ${line}`),
    `Rollback: remove the conditional forwarder for ${names.forwarder.zone}.`,
  ].join("\n");
}

/** The static records, one per line in zone-file form. */
export function staticRecordLines(names: GroupNames): string {
  return names.records.map((r) => `${r.name}. ${r.ttl} IN ${r.type} ${r.value}`).join("\n");
}
export type DraftRequest = z.infer<typeof edgeGroupDraftRequestSchema>;
export type DraftCapability = DraftRequest["capabilities"][number];
export type ChangeRequest = z.infer<typeof edgeGroupChangeRequestSchema>;
export type UpdateRequest = z.infer<typeof edgeGroupUpdateRequestSchema>;
export type Rollout = z.infer<typeof edgeGroupRolloutSchema>;
export type RolloutAction = "pause" | "resume" | "cancel";
export type ApprovalPolicy = z.infer<typeof edgeGroupApprovalPolicySchema>;
export type ApprovalPolicyRequest = { second_approver: boolean; include_updates: boolean };
export type ApprovalResult = z.infer<typeof edgeGroupApprovalResultSchema>;
export type RemovalRequest = z.infer<typeof edgeGroupRemovalRequestSchema>;
export type WithdrawalRequest = z.infer<typeof edgeGroupWithdrawalRequestSchema>;

/** The page's URL state: which group is open and which of its sections. */
export const edgeGroupsSearchSchema = z.object({
  group: z.string().uuid().optional().catch(undefined),
  section: z.enum(["configuration", "edges", "join-tokens", "history"]).optional().catch(undefined),
});
export type EdgeGroupsSearch = z.infer<typeof edgeGroupsSearchSchema>;
export type GroupSection = NonNullable<EdgeGroupsSearch["section"]>;

/** Every capability, in the order the page states them, with its one ordinary access option. */
export const capabilities: ReadonlyArray<{ id: Capability; name: string; description: string; option: string; optionName: string }> = [
  { id: "private_applications", name: "Private application access", description: "Governed browser access to assigned private applications.", option: "remote_access_v1", optionName: "Remote application access" },
  { id: "private_servers", name: "Private server access", description: "Governed SSH and RDP access to approved servers and accounts.", option: "privileged_remote_access_v1", optionName: "Remote server access" },
  { id: "agent_apis", name: "Agent API governance", description: "Delegated access to registered API actions.", option: "action_governance_v1", optionName: "Action approvals and consequence limits" },
  { id: "agent_outbound", name: "Agent outbound access", description: "Governed connections to registered destinations.", option: "spending_limits_v1", optionName: "Shared spending limits" },
];

export function capabilityName(id: Capability): string {
  return capabilities.find((c) => c.id === id)?.name ?? id;
}

/** Sentence-internal form of a name: only the first letter drops its capital, so "API" stays. */
export function lowerFirst(text: string): string {
  return `${text[0]?.toLowerCase() ?? ""}${text.slice(1)}`;
}

export function optionName(id: string): string {
  return capabilities.find((c) => c.option === id)?.optionName ?? id;
}

/** The service that serves a protected resource, by what the resource is. Core decides; this groups the picker. */
export function servingCapability(resource: { direction: string; private_access: boolean; protocol: string }): Capability {
  if (resource.direction === "egress") return "agent_outbound";
  if (resource.private_access) return resource.protocol === "ssh" || resource.protocol === "rdp" ? "private_servers" : "private_applications";
  return "agent_apis";
}

// ── Drain and lifetime choices ────────────────────────────────────────────────
/** Drain choices in minutes, 0 to 60. Zero closes established connections now. */
export const drainMinuteChoices = [0, 1, 5, 10, 15, 30, 60] as const;
export const DEFAULT_DRAIN_MINUTES = 5;
export function drainLabel(minutes: number): string {
  return minutes === 0 ? "Close now" : minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

/** Join token lifetimes in hours; four by default and at most 24. */
export const tokenLifetimeHourChoices = [1, 2, 4, 8, 12, 24] as const;
export const DEFAULT_TOKEN_LIFETIME_HOURS = 4;

// ── Forms (UI validation only; the request shapes are the contract's) ───────
export const joinTokenFormSchema = z.object({
  edges: z.number({ error: "Enter how many Edges" }).int("Enter a whole number").min(1, "At least 1 Edge").max(64, "At most 64 Edges"),
  lifetimeHours: z.number().int().min(1).max(24),
});
export type JoinTokenForm = z.infer<typeof joinTokenFormSchema>;
export function joinTokenRequest(form: JoinTokenForm): JoinTokenRequest {
  return { edges: form.edges, lifetime_seconds: form.lifetimeHours * 3600 };
}

export const removalFormSchema = z.object({
  drainMinutes: z.number().int().min(0).max(60),
  reason: z.string().trim().min(1, "Give a reason").max(500, "At most 500 characters"),
});
export type RemovalForm = z.infer<typeof removalFormSchema>;
export function removalRequest(form: RemovalForm): RemovalRequest {
  // Core records its observed count; a caller cannot assert the live impact.
  return { drain_seconds: form.drainMinutes * 60, reason: form.reason.trim() };
}

// ── Group state ───────────────────────────────────────────────────────────────
export function hasPendingItems(group: EdgeGroup): boolean {
  return group.pending_change !== null || group.pending_admissions.length > 0;
}

/**
 * The group is polled every 10 seconds only while something on it is actually moving: a pending
 * change or admission awaiting approval, or an Edge draining or waiting for its first certificate.
 * Otherwise it is not polled at all.
 */
/**
 * An open group is watched for its health every 60s; it is polled every 10s while something on it
 * is pending or moving.
 */
export function groupPollInterval(group: EdgeGroup | undefined): number | false {
  if (!group) return false;
  if (hasPendingItems(group) || group.retiring.length > 0 || group.rollout?.state === "running" || group.decommission_requested_at) return 10_000;
  const paused = new Set(group.configuration.capabilities.filter((capability) => capability.paused).map((capability) => capability.capability));
  const moving = group.edges.some((edge) => edge.state === "draining" || (edge.state !== "removed" && edge.state !== "withdrawn" && edge.workers.some((w) => (w.state === "awaiting_certificate" && !paused.has(w.capability)) || w.state === "draining")));
  return moving ? 10_000 : 60_000;
}

export function coverageLabel(coverage: Coverage): string {
  const count = `${coverage.serving} of ${coverage.edges} ${coverage.edges === 1 ? "Edge" : "Edges"} serving`;
  if (coverage.redundancy !== "none") return count;
  return coverage.edges > coverage.serving ? `Serving, no redundancy · ${count}` : "Serving, no redundancy";
}

export const edgeStateLabel: Record<GroupEdge["state"], string> = {
  serving: "Serving",
  unreachable: "Unreachable",
  admitted: "Admitted",
  draining: "Draining",
  removed: "Removed",
  withdrawn: "Withdrawn",
};

export function isLiveEdge(edge: GroupEdge): boolean {
  return edge.state === "serving" || edge.state === "unreachable" || edge.state === "admitted" || edge.state === "draining";
}

// ── Pending review ────────────────────────────────────────────────────────────
/**
 * What one passkey can approve right now. The change goes in unless separation of duties bars this
 * viewer from approving their own widening change. An admission minted for the pending change is
 * approved with it or not at all; an admission on the approved configuration stands alone.
 */
export function approvableItems(group: EdgeGroup, viewer: string | null): { change: GroupChange | null; admissions: Admission[]; waiting: Admission[]; ownWidening: boolean } {
  const pending = group.pending_change?.phase === "pending" ? group.pending_change : null;
  const ownWidening = pending !== null && pending.widens && group.second_approver && viewer !== null && pending.prepared_by === viewer;
  const change = pending && !ownWidening ? pending : null;
  const admissions: Admission[] = [];
  const waiting: Admission[] = [];
  for (const admission of group.pending_admissions) {
    if (admission.phase !== "pending") continue;
    if (admission.basis_change_id === null) admissions.push(admission);
    else if (change && admission.basis_change_id === change.change.id) admissions.push(admission);
    else waiting.push(admission);
  }
  return { change, admissions, waiting, ownWidening };
}

export type ChangeLine = { key: string; text: string };

export type PlacedResource = GroupChange["change"]["after"]["resources"][number];
export type ResourceChange = { id: string; before: PlacedResource | null; after: PlacedResource | null };

/** Diff the immutable approval artifact, including changed pins on an existing placement. */
export function resourceChanges(change: GroupChange): ResourceChange[] {
  const before = new Map(change.change.before.resources.map((resource) => [resource.id, resource]));
  const after = new Map(change.change.after.resources.map((resource) => [resource.id, resource]));
  return [...new Set([...before.keys(), ...after.keys()])].sort().flatMap((id) => {
    const was = before.get(id) ?? null;
    const next = after.get(id) ?? null;
    if (was && next && was.capability === next.capability && was.revision === next.revision && was.policy_sha256 === next.policy_sha256 && was.target_sha256 === next.target_sha256) return [];
    return [{ id, before: was, after: next }];
  });
}

export const sshCustodyName: Record<"hardware" | "kms" | "software", string> = { hardware: "hardware key", kms: "key management service key", software: "software key" };

/** The pending change in plain language: capabilities, options, minimums, SSH authority, resources and release. */
export function describeChange(change: GroupChange): ChangeLine[] {
  const { before, after } = change.change;
  const lines: ChangeLine[] = [];
  if (change.change.ticket) lines.push({ key: "ticket", text: `Change ticket ${change.change.ticket}` });
  for (const id of change.added_capabilities) lines.push({ key: `add-${id}`, text: `Turn on ${lowerFirst(capabilityName(id))}` });
  for (const id of change.removed_capabilities) {
    const drain = Math.round(change.change.drain_seconds / 60);
    lines.push({ key: `remove-${id}`, text: `Turn off ${lowerFirst(capabilityName(id))}; established connections ${drain === 0 ? "close at once" : `close within ${drainLabel(drain)}`}` });
  }
  for (const next of after.capabilities) {
    const prior = before.capabilities.find((c) => c.capability === next.capability);
    if ((prior?.paused ?? false) !== (next.paused ?? false)) {
      const drain = Math.round(change.change.drain_seconds / 60);
      lines.push({ key: `pause-${next.capability}`, text: next.paused
        ? `Pause ${lowerFirst(capabilityName(next.capability))}; keep its settings and resources; established connections ${drain === 0 ? "close at once" : `close within ${drainLabel(drain)}`}`
        : `Resume ${lowerFirst(capabilityName(next.capability))} with its retained settings and resources` });
    }
    for (const option of next.options) {
      if (!prior?.options.includes(option)) lines.push({ key: `on-${next.capability}-${option}`, text: `Allow ${lowerFirst(optionName(option))}` });
    }
    for (const option of prior?.options ?? []) {
      if (!next.options.includes(option)) lines.push({ key: `off-${next.capability}-${option}`, text: `Stop ${lowerFirst(optionName(option))}` });
    }
    const from = prior?.min_serving ?? 0;
    if (from !== next.min_serving) {
      lines.push({ key: `min-${next.capability}`, text: prior ? `Minimum serving Edges for ${lowerFirst(capabilityName(next.capability))}: ${from} to ${next.min_serving}` : `Minimum serving Edges for ${lowerFirst(capabilityName(next.capability))}: ${next.min_serving}` });
    }
    const was = prior?.ssh_authority;
    const now = next.ssh_authority;
    if (now && (was?.fingerprint !== now.fingerprint || was.custody !== now.custody)) {
      lines.push({ key: `ssh-${next.capability}`, text: `Servers trust the SSH authority ${now.fingerprint}, held as a ${sshCustodyName[now.custody]}` });
    } else if (was && !now) {
      lines.push({ key: `ssh-${next.capability}`, text: "Stop certificate sign-in to servers" });
    }
  }
  for (const { id } of capabilities) {
    const was = new Set(before.resources.filter((r) => r.capability === id).map((r) => r.id));
    const now = new Set(after.resources.filter((r) => r.capability === id).map((r) => r.id));
    const added = [...now].filter((r) => !was.has(r)).length;
    const removed = [...was].filter((r) => !now.has(r)).length;
    if (added > 0) lines.push({ key: `res-add-${id}`, text: `Place ${added} ${added === 1 ? "resource" : "resources"} on ${lowerFirst(capabilityName(id))}` });
    if (removed > 0) lines.push({ key: `res-remove-${id}`, text: `Remove ${removed} ${removed === 1 ? "resource" : "resources"} from ${lowerFirst(capabilityName(id))}` });
    const changed = resourceChanges(change).filter((resource) => resource.before?.capability === id && resource.after?.capability === id).length;
    if (changed > 0) lines.push({ key: `res-change-${id}`, text: `Change ${changed} ${changed === 1 ? "resource" : "resources"} on ${lowerFirst(capabilityName(id))}` });
  }
  const from = change.change.before_release;
  const to = change.change.release;
  if (!from) lines.push({ key: "release", text: `Run software ${to.version}` });
  else if (from.sha256 !== to.sha256) lines.push({ key: "release", text: `Update software from ${from.version} to ${to.version}` });
  if (lines.length === 0) lines.push({ key: "none", text: "Confirm the current configuration without changes" });
  return lines;
}

// ── Release rollouts ──────────────────────────────────────────────────────────
/** One line saying where the group's rollout is, for the software card. */
export function rolloutSummary(rollout: Rollout, edges: readonly GroupEdge[]): string {
  const target = `${rollout.rollback ? "Rolling back to" : "Updating to"} ${rollout.release_version}`;
  const progress = `${rollout.updated} of ${rollout.edges} ${rollout.edges === 1 ? "Edge" : "Edges"} updated`;
  const current = edges.find((edge) => edge.id === rollout.current_edge_id)?.name;
  switch (rollout.state) {
    case "running":
      return current ? `${target} · ${progress} · updating ${current}` : `${target} · ${progress}`;
    case "paused":
      return `${target} · paused · ${progress}`;
    case "halted":
      return `${target} · stopped: an Edge did not come back on the new release within 30 minutes · ${progress}`;
    case "completed":
      return `${rollout.rollback ? "Rolled back to" : "Updated to"} ${rollout.release_version} on every Edge`;
    case "cancelled":
      return `Update to ${rollout.release_version} cancelled · ${progress}`;
    case "superseded":
      return `Update to ${rollout.release_version} replaced by a later update`;
  }
}

export function rolloutOpen(rollout: Rollout | null | undefined): rollout is Rollout {
  return !!rollout && (rollout.state === "running" || rollout.state === "paused" || rollout.state === "halted");
}

export const healthLabel: Record<EdgeGroup["health"], string> = {
  serving: "Serving",
  degraded: "Degraded",
  down: "Down",
  unknown: "Not yet seen",
};

/**
 * What an update would cost before it is prepared: capabilities served by one Edge are interrupted
 * while it restarts; capabilities whose minimum equals their serving Edges cannot update any Edge
 * without going below it, so the rollout waits until an Edge is added or the minimum lowered.
 */
export function updateInterruption(group: EdgeGroup): { interrupts: Coverage[]; waits: Coverage[] } {
  return {
    interrupts: group.coverage.filter((c) => c.serving === 1),
    waits: group.coverage.filter((c) => c.serving > 1 && c.min_serving >= c.serving),
  };
}

/** A duration in plain words: seconds under two minutes, else minutes, else hours and minutes. */
export function durationLabel(seconds: number): string {
  if (seconds < 120) return `${Math.max(1, Math.round(seconds))} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 120) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hours` : `${hours} hours ${rest} minutes`;
}

/** What an update of the group is expected to take, and what the estimate rests on. */
export function updateDuration(group: EdgeGroup): string {
  const { edges, per_edge_seconds: each, measured_updates: measured } = group.update_estimate;
  const basis = measured > 0 ? `measured from the last ${measured} Edge ${measured === 1 ? "update" : "updates"}` : "estimated; no Edge has updated yet";
  return `Expected to take about ${durationLabel(edges * each)}: ${edges} ${edges === 1 ? "Edge" : "Edges"}, about ${durationLabel(each)} each, ${basis}.`;
}

export const changeKindLabel: Record<"configuration" | "update" | "rollback", string> = { configuration: "Configuration", update: "Update", rollback: "Rollback" };
export const changePhaseLabel: Record<"pending" | "approved" | "cancelled" | "expired", string> = { pending: "Awaiting approval", approved: "Approved", cancelled: "Cancelled", expired: "Expired" };
